import { useEffect, useMemo, useState } from 'react';
import type { LikelyToExtend, MonitorRow, User } from '../lib/types';
import { getAccessToken } from '../lib/auth';
import { formatDate, todayIso } from '../lib/format';
import {
  daysSinceContact,
  daysToEnd,
  likelyIsRed,
  mobileIsMissing,
  rowRag,
  urgencyScore,
  contactRag,
  type Rag,
} from '../lib/monitor';

/**
 * Contractor Monitor — styled with the Client Dashboard's design system
 * (index.css .ds-* classes) so the two apps read as one family.
 */

type LoadState =
  | { status: 'loading' }
  | { status: 'ok' }
  | { status: 'error'; message: string };

type SortKey = 'urgency' | 'status' | 'name' | 'client' | 'owner' | 'end' | 'likely' | 'mobile';
type StatusFilter = 'all' | Rag | 'never' | 'nomobile';

const LIKELY_LABEL: Record<Exclude<LikelyToExtend, null>, string> = {
  expected: 'Expected to extend',
  'not-expected': 'Not expected',
  unsure: 'Unsure',
  'not-discussed': 'Not discussed',
};

interface Toast {
  kind: 'success' | 'warning' | 'error';
  text: string;
}

/**
 * Status is never shown by colour alone (WCAG 1.4.1 — and Jason is colour
 * blind): every status carries its own icon shape and word.
 *   Overdue    → warning triangle
 *   Due soon   → clock
 *   Up to date → tick
 */
const RAG_LABEL: Record<Rag, string> = { red: 'Overdue', amber: 'Due soon', green: 'Up to date' };
const RAG_ORDER: Record<Rag, number> = { red: 2, amber: 1, green: 0 };

function StatusIcon({ rag }: { rag: Rag }) {
  const common = { viewBox: '0 0 16 16', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true };
  if (rag === 'red') {
    return (
      <svg {...common}>
        <path d="M8 1.8 L15 14.2 H1 Z" />
        <path d="M8 6.3 V9.6" />
        <path d="M8 11.9 V12" />
      </svg>
    );
  }
  if (rag === 'amber') {
    return (
      <svg {...common}>
        <circle cx="8" cy="8" r="6.3" />
        <path d="M8 4.4 V8 L10.6 9.6" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path d="M2.6 8.6 L6.4 12.2 L13.4 4" />
    </svg>
  );
}

function StatusBadge({ rag }: { rag: Rag }) {
  return (
    <span className={`ds-status-badge rag-${rag}`}>
      <StatusIcon rag={rag} />
      {RAG_LABEL[rag]}
    </span>
  );
}

async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const token = await getAccessToken();
  if (!token) throw new Error('Could not acquire a sign-in token — please reload.');
  return fetch(path, {
    ...init,
    headers: {
      ...(init.headers ?? {}),
      'X-User-Token': token,
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
    },
  });
}

async function readError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { reason?: string; message?: string };
    return body.reason ?? body.message ?? `HTTP ${res.status}`;
  } catch {
    return `HTTP ${res.status}`;
  }
}

interface Props {
  user: User;
}

export function ContractorMonitor({ user }: Props) {
  const today = todayIso();
  const [load, setLoad] = useState<LoadState>({ status: 'loading' });
  const [rows, setRows] = useState<MonitorRow[]>([]);
  const [ownerFilter, setOwnerFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('urgency');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [callTarget, setCallTarget] = useState<MonitorRow | null>(null);
  /** Placements with a Track Call still saving in the background. */
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set());
  const [toast, setToast] = useState<Toast | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authedFetch('/api/monitor');
        if (!res.ok) throw new Error(await readError(res));
        const body = (await res.json()) as { rows: MonitorRow[] };
        if (!cancelled) {
          setRows(body.rows);
          setLoad({ status: 'ok' });
        }
      } catch (err) {
        if (!cancelled) setLoad({ status: 'error', message: err instanceof Error ? err.message : String(err) });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), toast.kind === 'success' ? 5000 : 12000);
    return () => clearTimeout(t);
  }, [toast]);

  const showOwners = user.role !== 'self';

  // Rows for the selected owner — the tiles and the table both count from these.
  const ownerRows = useMemo(
    () => (ownerFilter === 'all' ? rows : rows.filter((r) => (r.assignmentOwner?.oid ?? 'none') === ownerFilter)),
    [rows, ownerFilter],
  );

  const counts = useMemo(() => {
    const c = { red: 0, amber: 0, green: 0, never: 0, nomobile: 0 };
    for (const r of ownerRows) {
      c[rowRag(r, today)] += 1;
      if (r.lastContacted === null) c.never += 1;
      if (mobileIsMissing(r)) c.nomobile += 1;
    }
    return c;
  }, [ownerRows, today]);

  // Breakdown by Candidate Owner (Mercury's Assignment Owner field): a stacked red / amber / green bar each.
  const owners = useMemo(() => {
    const map = new Map<string, { key: string; name: string; red: number; amber: number; green: number; total: number }>();
    for (const r of rows) {
      const key = r.assignmentOwner?.oid ?? 'none';
      const entry = map.get(key) ?? { key, name: r.assignmentOwner?.name ?? 'No candidate owner', red: 0, amber: 0, green: 0, total: 0 };
      entry[rowRag(r, today)] += 1;
      entry.total += 1;
      map.set(key, entry);
    }
    return [...map.values()].sort((a, b) => b.red - a.red || b.total - a.total || a.name.localeCompare(b.name));
  }, [rows, today]);
  const maxOwnerTotal = Math.max(1, ...owners.map((o) => o.total));

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = ownerRows.filter((r) => {
      if (statusFilter === 'never' && r.lastContacted !== null) return false;
      if (statusFilter === 'nomobile' && !mobileIsMissing(r)) return false;
      if ((statusFilter === 'red' || statusFilter === 'amber' || statusFilter === 'green') && rowRag(r, today) !== statusFilter) return false;
      if (q && !`${r.candidateName} ${r.clientName ?? ''} ${r.jobTitle}`.toLowerCase().includes(q)) return false;
      return true;
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      switch (sortKey) {
        case 'urgency': return dir * (urgencyScore(a, today) - urgencyScore(b, today));
        case 'status':
          return dir * (RAG_ORDER[rowRag(a, today)] - RAG_ORDER[rowRag(b, today)] || urgencyScore(a, today) - urgencyScore(b, today));
        case 'name': return dir * a.candidateName.localeCompare(b.candidateName);
        case 'client': return dir * (a.clientName ?? '').localeCompare(b.clientName ?? '');
        case 'owner': return dir * (a.assignmentOwner?.name ?? '').localeCompare(b.assignmentOwner?.name ?? '');
        case 'end': return dir * a.endDate.localeCompare(b.endDate);
        case 'likely': return dir * (Number(likelyIsRed(a, today)) - Number(likelyIsRed(b, today)));
        case 'mobile': return dir * (Number(mobileIsMissing(a)) - Number(mobileIsMissing(b)));
      }
    });
  }, [ownerRows, statusFilter, search, sortKey, sortDir, today]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else {
      setSortKey(key);
      setSortDir(key === 'urgency' || key === 'status' || key === 'likely' || key === 'mobile' ? 'desc' : 'asc');
    }
  };

  const toggleStatus = (s: StatusFilter) => setStatusFilter((cur) => (cur === s ? 'all' : s));

  const updateRow = (placementId: string, patch: Partial<MonitorRow>) =>
    setRows((prev) => prev.map((r) => (r.placementId === placementId ? { ...r, ...patch } : r)));

  const setPendingFor = (placementId: string, on: boolean) =>
    setPending((prev) => {
      const next = new Set(prev);
      if (on) next.add(placementId);
      else next.delete(placementId);
      return next;
    });

  /**
   * Optimistic Track Call: the row updates immediately and the save runs in
   * the background; on failure the row reverts and a message explains why.
   */
  const logCall = async (row: MonitorRow, note: string, likely: Exclude<LikelyToExtend, null>) => {
    const before = {
      lastContacted: row.lastContacted,
      lastContactType: row.lastContactType,
      likelyToExtend: row.likelyToExtend,
    };
    updateRow(row.placementId, { lastContacted: today, lastContactType: 'call', likelyToExtend: likely });
    setPendingFor(row.placementId, true);
    try {
      const res = await authedFetch('/api/monitor/call', {
        method: 'POST',
        body: JSON.stringify({ placementId: row.placementId, note, likelyToExtend: likely }),
      });
      const body = (await res.json().catch(() => ({}))) as {
        likelySaved?: boolean;
        likelyError?: string | null;
        reason?: string;
        message?: string;
      };
      if (!res.ok) {
        updateRow(row.placementId, {
          lastContacted: before.lastContacted,
          lastContactType: before.lastContactType,
          ...(body.likelySaved ? {} : { likelyToExtend: before.likelyToExtend }),
        });
        setToast({
          kind: 'error',
          text: `Call for ${row.candidateName} wasn't logged: ${body.reason ?? body.message ?? `HTTP ${res.status}`}`,
        });
        return;
      }
      if (body.likelyError) {
        updateRow(row.placementId, { likelyToExtend: before.likelyToExtend });
        setToast({
          kind: 'warning',
          text: `Call logged for ${row.candidateName}, but likely to extend wasn't saved: ${body.likelyError}`,
        });
        return;
      }
      setToast({ kind: 'success', text: `Call logged in Mercury for ${row.candidateName}` });
    } catch (err) {
      updateRow(row.placementId, before);
      setToast({
        kind: 'error',
        text: `Call for ${row.candidateName} wasn't logged: ${err instanceof Error ? err.message : String(err)}`,
      });
    } finally {
      setPendingFor(row.placementId, false);
    }
  };

  const ownerName = ownerFilter === 'all' ? null : owners.find((o) => o.key === ownerFilter)?.name ?? null;

  return (
    <main className="ds-page">
      <div className="ds-ph">
        <h1>
          Contractor monitor <em>who to call.</em>
        </h1>
        <div className="ds-meta">
          {load.status === 'ok' ? `${ownerRows.length} live contractors${ownerName ? ` · ${ownerName}` : ''} · ` : ''}
          last contacted = latest call or email on the timeline
        </div>
      </div>

      {load.status === 'loading' && (
        <div className="ds-panel" style={{ textAlign: 'center', padding: 48, color: 'var(--text-2)' }}>
          Loading contractors…
        </div>
      )}
      {load.status === 'error' && (
        <div className="ds-panel" style={{ borderLeft: '3px solid var(--rag-red)' }}>
          <h3>Couldn&rsquo;t load contractors</h3>
          <p style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text-2)' }}>{load.message}</p>
        </div>
      )}

      {load.status === 'ok' && (
        <>
          <div className="ds-heads">
            <Tile rag="red" label="Overdue" value={counts.red} note="over 30 days, never, or not discussed and ending soon" active={statusFilter === 'red'} onClick={() => toggleStatus('red')} />
            <Tile rag="amber" label="Due soon" value={counts.amber} note="last contact 11–30 days ago" active={statusFilter === 'amber'} onClick={() => toggleStatus('amber')} />
            <Tile rag="green" label="Up to date" value={counts.green} note="contacted in the last 10 days" active={statusFilter === 'green'} onClick={() => toggleStatus('green')} />
            <Tile rag="red" label="Never contacted" value={counts.never} note="no call or email on the timeline" active={statusFilter === 'never'} onClick={() => toggleStatus('never')} />
            <Tile rag="red" label="No mobile" value={counts.nomobile} note="add a number in the table" active={statusFilter === 'nomobile'} onClick={() => toggleStatus('nomobile')} />
          </div>

          {showOwners && owners.length > 1 && (
            <div className="ds-panel">
              <h3>
                By candidate owner
                <span>
                  {ownerFilter === 'all' ? 'click an owner to filter' : (
                    <button onClick={() => setOwnerFilter('all')} style={{ color: 'var(--perm-ink)' }}>
                      All owners · clear filter →
                    </button>
                  )}
                </span>
              </h3>
              <div className="ds-bars">
                {owners.map((o) => (
                  <button
                    key={o.key}
                    className={'ds-bar click' + (ownerFilter === o.key ? ' selected' : '')}
                    onClick={() => setOwnerFilter(ownerFilter === o.key ? 'all' : o.key)}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.name}</span>
                    <span
                      className="ds-track"
                      style={{ width: `${(o.total / maxOwnerTotal) * 100}%` }}
                      title={`${o.red} overdue · ${o.amber} due soon · ${o.green} up to date`}
                      aria-label={`${o.red} overdue, ${o.amber} due soon, ${o.green} up to date`}
                    >
                      <i style={{ width: `${(o.red / o.total) * 100}%`, background: 'var(--rag-red)' }} />
                      <i style={{ width: `${(o.amber / o.total) * 100}%`, background: 'var(--rag-amber-stripes)' }} />
                      <i style={{ width: `${(o.green / o.total) * 100}%`, background: 'transparent', boxShadow: 'inset 0 0 0 1.5px var(--rag-green)', borderRadius: 999 }} />
                    </span>
                    <b>{o.total}</b>
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 14, fontSize: 12, color: 'var(--text-2)' }}>
                <span>Bars, left to right:</span>
                {(['red', 'amber', 'green'] as const).map((rag) => (
                  <span key={rag} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <i
                      style={{
                        display: 'inline-block',
                        width: 18,
                        height: 10,
                        borderRadius: 3,
                        background: rag === 'red' ? 'var(--rag-red)' : rag === 'amber' ? 'var(--rag-amber-stripes)' : 'transparent',
                        boxShadow: rag === 'green' ? 'inset 0 0 0 1.5px var(--rag-green)' : rag === 'amber' ? 'inset 0 0 0 1px var(--rag-amber)' : 'none',
                      }}
                    />
                    <StatusBadge rag={rag} />
                  </span>
                ))}
                <span style={{ color: 'var(--text-3)' }}>hover a bar for exact counts</span>
              </div>
            </div>
          )}

          <div className="ds-sec">
            <h2>
              Contractors <em>{sortKey === 'urgency' && sortDir === 'desc' ? 'most overdue first.' : 'live today.'}</em>
            </h2>
            <span style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              {statusFilter !== 'all' && (
                <button onClick={() => setStatusFilter('all')} style={{ color: 'var(--perm-ink)' }}>
                  Showing {visible.length} · clear filter
                </button>
              )}
              <input
                className="ds-input"
                placeholder="Search contractor or client"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ width: 220 }}
              />
            </span>
          </div>

          <div className="ds-tw">
            <table className="ds-table">
              <thead>
                <tr>
                  <Th label="Contractor" k="name" sortKey={sortKey} dir={sortDir} onClick={toggleSort} />
                  <Th label="Status" k="status" sortKey={sortKey} dir={sortDir} onClick={toggleSort} />
                  <Th label="Client" k="client" sortKey={sortKey} dir={sortDir} onClick={toggleSort} />
                  {showOwners && <Th label="Candidate owner" k="owner" sortKey={sortKey} dir={sortDir} onClick={toggleSort} />}
                  <Th label="Ends" k="end" sortKey={sortKey} dir={sortDir} onClick={toggleSort} />
                  <th>Last contacted</th>
                  <Th label="Days since" k="urgency" sortKey={sortKey} dir={sortDir} onClick={toggleSort} />
                  <Th label="Likely to extend" k="likely" sortKey={sortKey} dir={sortDir} onClick={toggleSort} />
                  <th>Extension</th>
                  <Th label="Mobile" k="mobile" sortKey={sortKey} dir={sortDir} onClick={toggleSort} />
                  <th />
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => (
                  <MonitorTableRow
                    key={r.placementId}
                    row={r}
                    today={today}
                    showOwner={showOwners}
                    saving={pending.has(r.placementId)}
                    onTrackCall={() => setCallTarget(r)}
                    onMobileSaved={(mobile) => updateRow(r.placementId, { mobile, mobileSource: 'personal' })}
                  />
                ))}
                {visible.length === 0 && (
                  <tr>
                    <td colSpan={11} style={{ textAlign: 'center', padding: 40, color: 'var(--text-3)' }}>
                      No contractors match.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {callTarget && (
        <TrackCallDialog
          row={callTarget}
          onClose={() => setCallTarget(null)}
          onSubmit={(note, likely) => {
            const row = callTarget;
            setCallTarget(null);
            void logCall(row, note, likely);
          }}
        />
      )}

      {toast && (
        <button onClick={() => setToast(null)} className={'ds-toast' + (toast.kind === 'success' ? '' : ` ${toast.kind}`)}>
          {toast.text}
        </button>
      )}
    </main>
  );
}

function Tile({
  rag,
  label,
  value,
  note,
  active,
  onClick,
}: {
  rag: Rag;
  label: string;
  value: number;
  note: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button onClick={onClick} className={`ds-tile click rag-${rag}` + (active ? ' selected' : '')}>
      <span className="ds-lab">
        <StatusIcon rag={rag} />
        {label}
      </span>
      <span className="ds-val">{value}</span>
      <span className="ds-cmp">{note}</span>
    </button>
  );
}

function Th({
  label,
  k,
  sortKey,
  dir,
  onClick,
}: {
  label: string;
  k: SortKey;
  sortKey: SortKey;
  dir: 'asc' | 'desc';
  onClick: (k: SortKey) => void;
}) {
  const active = sortKey === k;
  return (
    <th className={active ? 'sorted' : ''}>
      <button onClick={() => onClick(k)}>
        {label}
        {active && <span style={{ color: 'var(--perm)', marginLeft: 4 }}>{dir === 'asc' ? '↑' : '↓'}</span>}
      </button>
    </th>
  );
}

function MonitorTableRow({
  row: r,
  today,
  showOwner,
  saving,
  onTrackCall,
  onMobileSaved,
}: {
  row: MonitorRow;
  today: string;
  showOwner: boolean;
  saving: boolean;
  onTrackCall: () => void;
  onMobileSaved: (mobile: string) => void;
}) {
  const days = daysSinceContact(r, today);
  const contact = contactRag(days);
  const likelyRed = likelyIsRed(r, today);
  const ending = daysToEnd(r, today);
  const status = rowRag(r, today);

  return (
    <tr className={`rag-${status}`}>
      <td>
        <div className="ds-name">
          {r.candidateName}
          <small>{r.jobTitle} · {r.idCode}</small>
        </div>
      </td>
      <td>
        <StatusBadge rag={status} />
      </td>
      <td className="trunc" style={{ color: 'var(--text-2)' }} title={r.clientName ?? undefined}>{r.clientName ?? '—'}</td>
      {showOwner && <td className="trunc" style={{ color: 'var(--text-2)' }}>{r.assignmentOwner?.name ?? '—'}</td>}
      <td className="num">
        {formatDate(r.endDate)}
        <small style={{ display: 'block', color: 'var(--text-3)', fontSize: 11 }}>
          {ending === 0 ? 'today' : `in ${ending} days`}
        </small>
      </td>
      <td className="num">
        {r.lastContacted ? formatDate(r.lastContacted) : <span style={{ color: 'var(--text-3)' }}>—</span>}
        {r.lastContactType && r.lastContactType !== 'other' && (
          <small style={{ display: 'block', color: 'var(--text-3)', fontSize: 11 }}>
            {r.lastContactType === 'call' ? 'Call' : 'Email'}
          </small>
        )}
      </td>
      <td>
        <span className={`ds-pill rag-${contact}`}>
          <StatusIcon rag={contact} />
          {days === null ? 'Never' : `${days}d`}
        </span>
      </td>
      <td>
        {likelyRed ? (
          <span className="ds-pill rag-red" style={{ fontFamily: 'var(--ui)' }}><StatusIcon rag="red" />Not discussed</span>
        ) : r.likelyToExtend === 'expected' ? (
          <span className="ds-pill rag-green" style={{ fontFamily: 'var(--ui)' }}><StatusIcon rag="green" />Expected</span>
        ) : (
          <span style={{ color: 'var(--text-2)' }}>{r.likelyToExtend ? LIKELY_LABEL[r.likelyToExtend] : '—'}</span>
        )}
      </td>
      <td>
        {r.extensionBooked ? <span className="ds-pill perm">Booked</span> : <span style={{ color: 'var(--text-4)' }}>—</span>}
      </td>
      <td>
        <MobileCell row={r} onSaved={onMobileSaved} />
      </td>
      <td style={{ textAlign: 'right' }}>
        <button className="ds-btn" onClick={onTrackCall} disabled={saving}>
          {saving ? 'Saving…' : 'Track call'}
        </button>
      </td>
    </tr>
  );
}

function MobileCell({ row, onSaved }: { row: MonitorRow; onSaved: (mobile: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (row.mobile && !editing) {
    return (
      <span style={{ fontFamily: 'var(--mono)' }}>
        <a href={`tel:${row.mobile.replace(/[^0-9+]/g, '')}`} style={{ color: 'var(--text-1)' }}>
          {row.mobile}
        </a>
        {row.mobileSource === 'work' && (
          <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--text-3)' }}>work</span>
        )}
      </span>
    );
  }

  if (!editing) {
    return (
      <span style={{ display: 'inline-flex', gap: 6 }}>
        <span className="ds-pill rag-red" style={{ fontFamily: 'var(--ui)' }}><StatusIcon rag="red" />Missing</span>
        <button className="ds-pill action" onClick={() => { setEditing(true); setError(null); }}>
          Add
        </button>
      </span>
    );
  }

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await authedFetch('/api/monitor/mobile', {
        method: 'POST',
        body: JSON.stringify({ placementId: row.placementId, mobile: draft }),
      });
      if (!res.ok) throw new Error(await readError(res));
      onSaved(draft.trim());
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
        <input
          autoFocus
          className="ds-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void save();
            if (e.key === 'Escape') setEditing(false);
          }}
          placeholder="Personal mobile"
          style={{ width: 150, fontFamily: 'var(--mono)' }}
        />
        <button className="ds-pill action" onClick={() => void save()} disabled={saving || draft.trim().length < 6}>
          {saving ? '…' : 'Save'}
        </button>
      </span>
      {error && <span style={{ fontSize: 11, color: 'var(--rag-red-ink)' }}>{error}</span>}
    </span>
  );
}

function TrackCallDialog({
  row,
  onClose,
  onSubmit,
}: {
  row: MonitorRow;
  onClose: () => void;
  /** Hands the details to the parent, which saves in the background. */
  onSubmit: (note: string, likely: Exclude<LikelyToExtend, null>) => void;
}) {
  const [note, setNote] = useState('');
  const [likely, setLikely] = useState<Exclude<LikelyToExtend, null>>(row.likelyToExtend ?? 'not-discussed');

  return (
    <div className="ds-modal-backdrop" onClick={onClose}>
      <div className="ds-modal" onClick={(e) => e.stopPropagation()}>
        <header style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 4 }}>Track call · Contractor Monitor Call</div>
            <div style={{ fontSize: 20, fontWeight: 500, letterSpacing: '-0.01em' }}>{row.candidateName}</div>
            <div style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 2 }}>
              {row.jobTitle}{row.clientName ? ` · ${row.clientName}` : ''}
            </div>
            {row.mobile && (
              <a
                href={`tel:${row.mobile.replace(/[^0-9+]/g, '')}`}
                style={{ display: 'inline-block', marginTop: 8, fontFamily: 'var(--mono)', color: 'var(--perm-ink)' }}
              >
                {row.mobile}
              </a>
            )}
          </div>
          <button onClick={onClose} aria-label="Close" style={{ color: 'var(--text-3)', fontSize: 22, lineHeight: 1, alignSelf: 'flex-start' }}>
            ×
          </button>
        </header>

        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label htmlFor="likely">Likely to extend</label>
            <select id="likely" value={likely} onChange={(e) => setLikely(e.target.value as Exclude<LikelyToExtend, null>)}>
              {(Object.keys(LIKELY_LABEL) as Array<Exclude<LikelyToExtend, null>>).map((k) => (
                <option key={k} value={k}>{LIKELY_LABEL[k]}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="note">Note (optional)</label>
            <textarea
              id="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
              maxLength={2000}
              placeholder="How are they getting on? Any extension or redeployment news?"
              style={{ resize: 'none' }}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button className="ds-btn" onClick={onClose}>Cancel</button>
            <button className="ds-btn primary" onClick={() => onSubmit(note.trim(), likely)}>
              Log call in Mercury
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
